import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAuthApiUrl, loginSession } from '../../../app/services/session.service';
import { Alert, AlertDescription, AlertTitle } from '../../../shared/ui/alert';
import { Button } from '../../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/ui/card';
import { Input } from '../../../shared/ui/input';
import { Label } from '../../../shared/ui/label';

export default function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadInvite() {
      setIsLoading(true);
      setError('');

      try {
        const response = await fetch(getAuthApiUrl(`/api/auth/invites/${token}`));
        const payload = await response.json().catch(() => null);
        if (!active) return;

        if (!response.ok) {
          setError(payload?.message || 'Invito non valido o scaduto.');
          setInvite(null);
          return;
        }

        setInvite(payload);
      } catch {
        if (active) {
          setError('Backend auth non disponibile.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    loadInvite();

    return () => {
      active = false;
    };
  }, [token]);

  const isWorkspaceUserInvite = invite && !['TENANT_ADMIN', 'OWNER'].includes(String(invite.role || '').toUpperCase());

  async function acceptInvite(event) {
    event.preventDefault();
    setError('');

    if (isWorkspaceUserInvite) {
      if (!initialPassword) {
        setError('Inserisci la password iniziale ricevuta dall owner.');
        return;
      }
      if (!newPassword || newPassword.length < 8) {
        setError('Inserisci una nuova password di almeno 8 caratteri.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Le password non coincidono.');
        return;
      }
    } else {
      if (!password || password.length < 8) {
        setError('Inserisci una password di almeno 8 caratteri.');
        return;
      }

      if (password !== confirmPassword) {
        setError('Le password non coincidono.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(getAuthApiUrl(`/api/auth/invites/${token}/accept`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(isWorkspaceUserInvite
          ? { initialPassword, newPassword, confirmNewPassword: confirmPassword }
          : { password }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        setError(payload?.message || 'Attivazione invito non completata.');
        return;
      }

      if (isWorkspaceUserInvite) {
        await loginSession({ email: invite.email, password: newPassword });
        navigate('/', { replace: true });
        return;
      }

      setAccepted(true);
      setPassword('');
      setInitialPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setError('Backend auth non disponibile.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--surface-raised)] px-6 py-12">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-xl items-center justify-center">
        <Card className="w-full border-[var(--border)] bg-[var(--surface-raised)] shadow-sm">
          <CardHeader>
            <CardTitle className="text-3xl text-[var(--text-primary)]">
              {isWorkspaceUserInvite ? 'Completa invito workspace' : 'Attiva account owner'}
            </CardTitle>
            <CardDescription>
              Imposta la password per completare l&apos;invito workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? <p className="text-sm text-[var(--text-secondary)]">Verifica invito...</p> : null}

            {error ? (
              <Alert className="mb-6 border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text-primary)]">
                <AlertTitle>Invito non disponibile</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            {accepted ? (
              <Alert variant="success" className="mb-6">
                <AlertTitle>Account attivato</AlertTitle>
                <AlertDescription>
                  Ora puoi accedere con la tua email e la password appena impostata.
                </AlertDescription>
              </Alert>
            ) : null}

            {invite && !accepted ? (
              <form className="space-y-5" onSubmit={acceptInvite}>
                <div className="rounded-md border border-[var(--border)] bg-[var(--surface-raised)] p-4 text-sm text-[var(--text-primary)]">
                  <div><strong>Workspace:</strong> {invite.tenantName || invite.tenantId}</div>
                  <div><strong>Email:</strong> {invite.email}</div>
                  <div><strong>Scadenza:</strong> {invite.expiresAt || 'non disponibile'}</div>
                </div>

                {isWorkspaceUserInvite ? (
                  <>
                    <div>
                      <Label htmlFor="invite-initial-password">Password iniziale</Label>
                      <Input
                        id="invite-initial-password"
                        type="password"
                        value={initialPassword}
                        onChange={(event) => setInitialPassword(event.target.value)}
                        className="mt-2"
                        autoComplete="current-password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="invite-new-password">Nuova password</Label>
                      <Input
                        id="invite-new-password"
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        className="mt-2"
                        autoComplete="new-password"
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <Label htmlFor="invite-password">Password</Label>
                    <Input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="mt-2"
                      autoComplete="new-password"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="invite-confirm-password">Conferma password</Label>
                  <Input
                    id="invite-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="mt-2"
                    autoComplete="new-password"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Attivazione...' : 'Attiva account'}
                </Button>
              </form>
            ) : null}

            {accepted ? (
              <Link className="text-sm font-medium text-[var(--success-text)] underline" to="/login">
                Vai al login
              </Link>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
